import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Columns3,
  GaugeCircle,
  Home,
  IndianRupee,
  LayoutList,
  ListChecks,
  LogOut,
  School,
  Star,
  Table2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BrandLockup, PoweredByAdmissionHall } from "@/components/Brand";
import { DataAvailabilityBanner } from "@/components/DataAvailabilityBanner";

const TABS = [
  { to: "/app", label: "Overview", icon: Home, end: true },
  { to: "/app/allotment", label: "Allotment", icon: LayoutList },
  { to: "/app/closing-ranks", label: "Closing Ranks", icon: Table2 },
  { to: "/app/fees", label: "Fees", icon: IndianRupee },
  { to: "/app/colleges", label: "College Details", icon: School },
  { to: "/app/probability", label: "Probability", icon: GaugeCircle },
  { to: "/app/compare", label: "Compare", icon: Columns3 },
  { to: "/app/watchlist", label: "Watchlist", icon: Star },
  { to: "/app/saved", label: "Saved", icon: ListChecks },
];

export default function AppLayout() {
  const { user, logout, isPaid } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-[1600px] items-center gap-6 px-4">
          <Link to="/app" data-testid="app-brand" aria-label="NeetOne home" className="flex items-center gap-2">
            <BrandLockup size="md" wordmark="hide" />
            <PoweredByAdmissionHall className="hidden lg:inline" />
          </Link>
          <div className="ml-auto flex items-center gap-3">
            {isPaid ? (
              <span
                data-testid="plan-badge"
                className="border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] font-semibold uppercase tracking-widest text-emerald-800"
              >
                MasterPlan
              </span>
            ) : (
              <Link
                data-testid="upgrade-nav-btn"
                to="/app/upgrade"
                className="border border-[#1A365D] bg-[#1A365D] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#122845]"
              >
                Upgrade ₹999
              </Link>
            )}
            {user?.role === "admin" && (
              <Link
                data-testid="admin-panel-link"
                to="/admin"
                className="text-xs font-semibold uppercase tracking-widest text-slate-500 hover:text-[#1A365D]"
              >
                Admin
              </Link>
            )}
            <span className="hidden text-xs text-slate-500 sm:inline" data-testid="current-user-label">
              {user?.name || user?.email || `+${user?.phone}`}
            </span>
            <button
              data-testid="logout-btn"
              onClick={async () => {
                await logout();
                nav("/");
              }}
              className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-rose-600"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
        <nav className="mx-auto flex max-w-[1600px] gap-1 overflow-x-auto px-4">
          {TABS.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              data-testid={`tab-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-wide transition-colors ${
                  isActive
                    ? "border-[#1A365D] text-[#1A365D]"
                    : "border-transparent text-slate-500 hover:text-slate-900"
                }`
              }
            >
              <t.icon className="h-3.5 w-3.5" /> {t.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <DataAvailabilityBanner className="mx-auto max-w-[1600px]" />
      <main className="mx-auto max-w-[1600px] space-y-4 px-4 py-5">
        <Outlet />
      </main>
    </div>
  );
}
